// Bildschirm-Zustand eines LG-Geraets - der Ersatz fuer das, was auf Samsung
// der Power-Listener liefert.
//
// AUSGANGSLAGE (steht so schon in main.js und keyHandler/LG.js): auf LG gibt es
// keinen brauchbaren PowerStateListener, und auf `visibilitychange` ist beim
// EINSCHLAFEN kein Verlass - deshalb blieb im SocketAdmin bei LG-Geraeten
// dauerhaft "Bildschirm an" stehen, egal ob der TV im Zimmer dunkel war.
//
// WAS ES DAFUER GIBT, in dieser Reihenfolge:
//
//  1. Das hcap-EREIGNIS `power_mode_changed`. hcap.js verteilt jede Meldung mit
//     command_id "event" als DOM-Ereignis auf `document` (siehe static/hcap.js:
//     `r=document.createEvent("HTMLEvents"); r.initEvent(command,...)`), ganz
//     ohne Anmeldung. Die LG-Referenzimplementierung in lib/deviceApi (Zweig
//     'power_mode_changed') macht genau das und fragt danach den Modus ab.
//  2. hcap.video.isVideoMute - Bild stumm, also schwarzer Schirm bei laufendem
//     Geraet. Nicht jedes Modell kann es: ein 43UK660H antwortet darauf
//     dauerhaft mit "Hcap::instance()->get_video_mute() failed".
//  3. hcap.power.getPowerMode - NORMAL(1) oder WARM(2).
//
// WARUM getPowerMode ALLEIN NICHTS SAGT: auf dem 43UK660H steht der Wert auch
// nach der Power-Taste unveraendert auf 2 (WARM) - derselbe Wert wie im
// Betrieb. Eine abgefragte Zahl ohne vorangegangenes Ereignis ist damit keine
// Aussage ueber den Bildschirm. Sie wird deshalb erst ausgewertet, NACHDEM
// einmal `power_mode_changed` kam; bis dahin faehrt sie nur als Rohwert mit.
//
// WARUM DIE SONDE VON SELBST VERSTUMMT: hcap.js schreibt zu jedem Kommando drei
// console-Zeilen (added/sent/received), und der Live-Log im Admin spiegelt
// console.* (store/modules/socket.js). Ein 10-Sekunden-Takt auf einem Geraet,
// das die Frage gar nicht beantworten kann, ergibt damit sechs Logzeilen alle
// zehn Sekunden und keinen einzigen Messwert. Kann ein Modell isVideoMute
// nicht, wird das Abfragen deshalb dauerhaft eingestellt - die Sonde haengt
// dann nur noch am Ereignis und ist im Log still.

import DeviceType from '@/helpers/DeviceType';
import { hasHcapMethod, hcapCall } from '@/helpers/LgHcap';

const POLL_MS = 10000;

// Kurz halten: ein Aufruf, der laenger braucht, ist in der Ausschaltsequenz
// stecken geblieben - und genau dann wollen wir die Antwort "nicht erreichbar"
// und keine haengende Abfrage.
const CALL_TIMEOUT_MS = 1500;

// Drei Fehlschlaege hintereinander sind kein Zufall mehr, sondern das Modell.
// Drei und nicht einer, damit ein einzelner Aussetzer waehrend der
// Ausschaltsequenz die Quelle nicht dauerhaft wegnimmt.
const VIDEO_MUTE_MAX_FAILS = 3;

// Namen der hcap-Ereignisse, so wie hcap.js sie auf document feuert.
const EV_POWER_MODE = 'power_mode_changed';
const EV_APP_FOCUS = 'hcap_application_focus_changed';
// Die Firmware raeumt die App ab. Am US662 (webOS 4.5) im Log gesehen:
//
//   13:04:47.447  power_mode_changed
//   13:04:49.520  on_destroy
//
// Danach ist Schluss - anders als im Warm-Standby eines 43UK660H ueberlebt die
// App hier nicht. Deshalb ist das die LETZTE Gelegenheit, den Standby zu
// melden; eine Abfrage waere zu langsam, die Antwort kaeme nie zurueck.
const EV_DESTROY = 'on_destroy';

let timer = null;
let onChange = null;
let started = false;
let listening = false;
let videoMuteFails = 0;
let videoMuteUsable = true;

// Seit dem letzten Abholen wurde der Bildschirm mindestens einmal als AUS
// gemessen. Siehe apply() und LgScreen.takeStandbyMark().
let sawStandby = false;

/** Der zuletzt gelesene Stand. Alles null, solange nichts gemessen wurde. */
const state = {
  screen: null,
  videoMute: null,
  powerMode: null,
  // Erst ein Ereignis macht aus der Zahl eine Aussage - siehe Kopf.
  powerModeLive: false,
  // Zaehler fuer die Ferndiagnose: feuert dieses Modell die Ereignisse
  // ueberhaupt? Das laesst sich damit aus der Zustandsmeldung ablesen, ohne
  // jemanden vor das Geraet zu schicken.
  powerEvents: 0,
  focusEvents: 0,
  // Richtung des letzten Fokuswechsels ('focused' / 'unfocused') und wann er
  // kam. Siehe handleFocusEvent - vorerst reine Beobachtung.
  lastFocus: null,
  lastFocusTs: null,
  // Die Firmware hat die App abgeraeumt (on_destroy). Danach gilt 'off' und
  // sonst nichts mehr - siehe handleDestroyEvent.
  destroyed: false,
  ts: null,
};

function readVideoMute() {
  if (!videoMuteUsable || !hasHcapMethod('video', 'isVideoMute')) return Promise.resolve(null);
  return hcapCall('video', 'isVideoMute', {}, CALL_TIMEOUT_MS)
    .then((s) => {
      videoMuteFails = 0;
      if (s === null || s === undefined) return null;
      if (typeof s === 'boolean') return s;
      // Je nach Firmware {isVideoMute:true}, {videoMute:1} oder {value:...}.
      const raw =
        s.isVideoMute !== undefined
          ? s.isVideoMute
          : s.videoMute !== undefined
            ? s.videoMute
            : s.mute !== undefined
              ? s.mute
              : s.value;
      if (raw === undefined || raw === null) return null;
      if (typeof raw === 'boolean') return raw;
      if (typeof raw === 'number') return raw !== 0;
      const v = String(raw).trim().toLowerCase();
      if (v === 'on' || v === 'true' || v === '1') return true;
      if (v === 'off' || v === 'false' || v === '0') return false;
      return null;
    })
    .catch(() => {
      videoMuteFails += 1;
      if (videoMuteFails >= VIDEO_MUTE_MAX_FAILS && videoMuteUsable) {
        videoMuteUsable = false;
        // Eine Zeile, einmal - damit im Log steht, warum von hier an nichts
        // mehr kommt. Das ist die Antwort auf "die Sonde meldet nichts".
        console.log(
          '[LgScreen] isVideoMute wird von diesem Modell nicht beantwortet (' +
            videoMuteFails +
            ' Fehlschlaege) - Abfrage eingestellt, es bleibt das Ereignis ' +
            EV_POWER_MODE
        );
        stopTimer();
      }
      return null;
    });
}

function readPowerMode() {
  if (!hasHcapMethod('power', 'getPowerMode')) return Promise.resolve(null);
  return hcapCall('power', 'getPowerMode', {}, CALL_TIMEOUT_MS)
    .then((s) => {
      if (s === null || s === undefined) return null;
      if (typeof s !== 'object') return s;
      return s.mode !== undefined ? s.mode : s.powerMode !== undefined ? s.powerMode : s.value;
    })
    // Keine Antwort heisst hier etwas: in der Ausschaltsequenz verstummt die
    // Methode. Als eigener Wert, damit er sich von "gibt es nicht" (null)
    // unterscheidet.
    .catch(() => 'keine Antwort');
}

// Aus den Rohwerten den Bildschirm-Zustand bilden und bei einer AENDERUNG
// melden. Reihenfolge: der gemessene Bildwert schlaegt den Modus, und ein
// Modus zaehlt nur, wenn ein Ereignis ihn beglaubigt hat.
function apply(videoMute, powerMode) {
  // Nach on_destroy wird nichts mehr umgeschrieben: eine noch unterwegs
  // befindliche Antwort darf den letzten gueltigen Stand nicht auf 'on'
  // zuruecksetzen, waehrend das Geraet ausgeht.
  if (state.destroyed) return state;
  let screen = null;
  if (videoMute === true) screen = 'off';
  else if (videoMute === false) screen = 'on';
  else if (state.powerModeLive) {
    // hcap.power.PowerMode = {NORMAL:1, WARM:2} (static/hcap.js). WARM ist der
    // Instant-On-Standby, in den auch Device/LG.js turnOff() schaltet.
    if (powerMode === 1) screen = 'on';
    else if (powerMode === 2) screen = 'off';
  }

  // Einmal 'off' gesehen zu haben ist eine Tatsache, die bleibt, bis jemand sie
  // abholt. Der cast-Store braucht sie als BEWEIS, dass der TV wirklich aus war
  // - auf LG ist 'hidden' allein mehrdeutig (Standby oder eine Fremd-App im
  // Vordergrund), und ein Fehlurteil meldet den Gast mitten in seiner Sitzung
  // ab. Siehe cast/noteStandbyIfSoftApDown.
  if (screen === 'off') sawStandby = true;

  const changed = screen !== state.screen || powerMode !== state.powerMode;
  state.screen = screen;
  state.videoMute = videoMute;
  state.powerMode = powerMode;
  state.ts = Date.now();
  if (changed && typeof onChange === 'function') {
    try {
      onChange(Object.assign({}, state));
    } catch (e) {
      /* der Melder darf die Sonde nicht anhalten */
    }
  }
  return state;
}

// Der Takt: nur der Bildwert. Der Modus haengt am Ereignis und wird hier NICHT
// mitgefragt - er aendert sich ohne Ereignis ohnehin nicht und kostete nur die
// halbe Logflut.
function poll() {
  return readVideoMute().then((videoMute) => apply(videoMute, state.powerMode));
}

// Der Ereignisweg: beides frisch lesen. Nach `power_mode_changed` ist der Modus
// per Definition aktuell, deshalb darf er ab hier entscheiden.
function refresh() {
  return Promise.all([readVideoMute(), readPowerMode()]).then((res) => apply(res[0], res[1]));
}

function stopTimer() {
  if (timer) clearInterval(timer);
  timer = null;
}

function startTimer() {
  if (timer) return;
  // Ohne beantwortbare Frage kein Takt - siehe Kopf.
  if (!videoMuteUsable || !hasHcapMethod('video', 'isVideoMute')) return;
  timer = setInterval(() => {
    poll().catch(() => undefined);
  }, POLL_MS);
}

function handlePowerModeEvent() {
  state.powerEvents += 1;
  state.powerModeLive = true;
  console.log('[LgScreen] hcap-Ereignis ' + EV_POWER_MODE + ' (' + state.powerEvents + '.)');
  refresh().catch(() => undefined);
}

// Das Ereignis sagt MEHR, als bisher angenommen: es traegt eine Richtung.
//
// Am Geraet im Log gesehen (43UK660H):
//   12:22:53  eventType "unfocused"  - YouTube kam in den Vordergrund
//   12:23:44  eventType "focused"    - unsere App war zurueck
//
// Bis hierher wurde nur gezaehlt, weil nicht bekannt war, dass sich die beiden
// Richtungen unterscheiden lassen. Damit waere messbar, was der cast-Store
// bisher aus softApActive ERSCHLIESSEN muss: dass eine Fremd-App im
// Vordergrund war, also jemand gecastet hat.
//
// NOCH NICHT AUSGEWERTET - erst messen. Was der Fokusverlust alles bedeuten
// kann (Standby? Overlay der Firmware? Lautstaerke-OSD?), ist nicht vermessen,
// und ein Fehlurteil loescht im Zweifel die Anmeldung eines Gastes mitten in
// seiner Sitzung. Richtung und Zeitpunkt stehen deshalb erst einmal nur im Log
// und in der Zustandsmeldung.
function handleFocusEvent(ev) {
  state.focusEvents += 1;
  const type = (ev && ev.eventType) || '';
  if (type) {
    state.lastFocus = String(type);
    state.lastFocusTs = Date.now();
  }
  console.log(
    '[LgScreen] hcap-Ereignis ' + EV_APP_FOCUS + ': ' + (type || 'ohne Typ') +
    ' (' + state.focusEvents + '.)'
  );
}

// Die App wird abgeraeumt - der Bildschirm ist aus, und zwar SICHER.
//
// Hier wird nichts mehr abgefragt: zwischen on_destroy und dem Ende bleiben
// Millisekunden, eine hcap-Antwort kaeme nicht mehr zurueck. Der Zustand wird
// direkt gesetzt und sofort gemeldet, damit die letzte Zustandsmeldung an den
// Socket noch "Bildschirm aus" traegt.
//
// DER ANLASS: am US662 blieb im SocketAdmin kein Standby stehen - der Eintrag
// verschwand einfach. Genau das passiert, wenn die App stirbt, ohne vorher
// etwas gemeldet zu haben: der Server sieht nur noch ein Geraet, das nicht mehr
// antwortet, und kann nicht unterscheiden, ob es im Standby oder vom Netz ist.
function handleDestroyEvent() {
  console.log('[LgScreen] hcap-Ereignis ' + EV_DESTROY + ' - die App wird beendet, Bildschirm ist aus');
  sawStandby = true;
  state.screen = 'off';
  state.destroyed = true;
  state.ts = Date.now();
  if (typeof onChange === 'function') {
    try {
      onChange(Object.assign({}, state));
    } catch (e) {
      /* der Melder darf nichts anhalten - es ist ohnehin der letzte Moment */
    }
  }
}

function attachEvents() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener(EV_POWER_MODE, handlePowerModeEvent);
  document.addEventListener(EV_APP_FOCUS, handleFocusEvent);
  document.addEventListener(EV_DESTROY, handleDestroyEvent);
}

function detachEvents() {
  if (!listening || typeof document === 'undefined') return;
  listening = false;
  document.removeEventListener(EV_POWER_MODE, handlePowerModeEvent);
  document.removeEventListener(EV_APP_FOCUS, handleFocusEvent);
  document.removeEventListener(EV_DESTROY, handleDestroyEvent);
}

export default class LgScreen {
  /** Der zuletzt gemessene Bildschirm-Zustand: 'on', 'off' oder null. */
  static get screen() {
    return state.screen;
  }

  /**
   * War der Bildschirm seit dem letzten Aufruf nachweislich AUS? Holt die
   * Antwort ab und setzt den Merker zurueck.
   *
   * Gedacht fuer den Wake-Pfad im cast-Store: dort entscheidet sich, ob die
   * Anmeldedaten des Gastes geloescht werden. Ein 'hidden' allein reicht dafuer
   * auf LG nicht - es steht genauso fuer eine Fremd-App im Vordergrund, und ein
   * Fehlurteil meldet den Gast mitten in seiner Sitzung ab. Eine GEMESSENE
   * Bildschirmabschaltung ist dagegen ein Beweis.
   *
   * Liefert false, solange die Sonde nichts messen konnte - dann gilt weiter,
   * was der Aufrufer sonst weiss (soft_ap == 0).
   */
  static takeStandbyMark() {
    const seen = sawStandby;
    sawStandby = false;
    return seen;
  }

  /** Die Rohwerte fuer die Zustandsmeldung und die Info-Seite. */
  static get raw() {
    return {
      videoMute: state.videoMute,
      powerMode: state.powerMode,
      powerModeLive: state.powerModeLive,
      powerEvents: state.powerEvents,
      focusEvents: state.focusEvents,
      lastFocus: state.lastFocus,
      lastFocusTs: state.lastFocusTs,
      destroyed: state.destroyed,
      videoMuteUsable: videoMuteUsable,
      ts: state.ts,
    };
  }

  /**
   * Sonde starten. Mehrfach aufzurufen ist harmlos - es laeuft genau eine.
   * `notify` wird nur bei einer AENDERUNG gerufen, nicht bei jedem Durchlauf.
   */
  static start(notify) {
    if (!DeviceType.isLG) return;
    onChange = notify || onChange;
    attachEvents();
    if (started) return;
    started = true;
    // Einmal sofort, damit der erste Zustandsbericht nach dem Verbinden schon
    // stimmt und nicht zehn Sekunden lang "an" behauptet. Der Modus faehrt
    // dabei als Grundwert mit, wird aber erst nach dem ersten Ereignis
    // ausgewertet.
    refresh().catch(() => undefined);
    startTimer();
  }

  static stop() {
    stopTimer();
    detachEvents();
    started = false;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/LgScreen.js